import React, {
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import HoldAndWinCollectorDisplayController from "./HoldAndWinCollectorDisplayController";
import { safeInit } from "../../../utilities";
import { selectHoldAndWinActive, selectIsHoldAndWinCompletionReload } from "../../../State/selectors";

/**
 * HoldAndWinCollectorDisplay - Displays collector spine animation
 * Shows when collection starts (replaces counter)
 */
const HoldAndWinCollectorDisplay = forwardRef((props, ref) => {
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const isHoldAndWinCompletionReload = useSelector(selectIsHoldAndWinCompletionReload);

  const collectorDisplayControllerRef = useRef(null);
  const collectorDisplayContainerRef = useRef(null);

  const dimensionsContext = useDimensions();
  const { dimensions, symbolHeight, device, orientation } = dimensionsContext;

  // Get device-specific Y offset
  const yOffset = useMemo(() => {
    const config = HoldAndWinCollectorDisplayController.getConfigForDevice(
      device,
      orientation
    );
    return symbolHeight * config.yOffsetMultiplier;
  }, [device, orientation, symbolHeight]);

  // Initialize controller when container is ready and Hold and Win becomes active
  useEffect(() => {
    if (
      collectorDisplayContainerRef.current &&
      !collectorDisplayControllerRef.current &&
      holdAndWinActive
    ) {
      safeInit(() => {
        collectorDisplayControllerRef.current =
          new HoldAndWinCollectorDisplayController(
            collectorDisplayContainerRef.current,
            dimensions,
            symbolHeight,
            device,
            orientation
          );

        // Don't show collector here - let the showHoldAndWinCollector effect handle it
      });
    }
  }, [holdAndWinActive, dimensions, symbolHeight, device, orientation]);

  // Handle dimension changes
  useEffect(() => {
    if (collectorDisplayControllerRef.current && symbolHeight && dimensions) {
      collectorDisplayControllerRef.current.updateDimensions(
        dimensions,
        symbolHeight,
        device,
        orientation
      );
    }
  }, [symbolHeight, dimensions, device, orientation]);

  // NOTE: We don't auto-show/hide collector based on showHoldAndWinCollector state
  // The collector is shown/hidden manually via imperative methods from the collection controller
  // showHoldAndWinCollector is only used to hide the counter component

  // Memoize controller methods
  const getController = useCallback(() => {
    return collectorDisplayControllerRef.current;
  }, []);

  const showCollector = useCallback(() => {
    collectorDisplayControllerRef.current?.showCollector();
  }, []);

  const hideCollector = useCallback(() => {
    collectorDisplayControllerRef.current?.hideCollector();
  }, []);

  const updateCollectorValue = useCallback((value) => {
    collectorDisplayControllerRef.current?.updateCollectorValue(value);
  }, []);

  const enableSkipMode = useCallback(() => {
    collectorDisplayControllerRef.current?.enableSkipMode();
  }, []);

  const disableSkipMode = useCallback(() => {
    collectorDisplayControllerRef.current?.disableSkipMode();
  }, []);

  const setMaxWinCheck = useCallback((threshold, callback) => {
    collectorDisplayControllerRef.current?.setMaxWinCheck(threshold, callback);
  }, []);

  const startCounterAnimation = useCallback((toValue, duration, onComplete) => {
    collectorDisplayControllerRef.current?.startCounterAnimation(toValue, duration, onComplete);
  }, []);

  const isCounterAnimating = useCallback(() => {
    return collectorDisplayControllerRef.current?.isCounterAnimating() || false;
  }, []);

  const stopCounterAnimation = useCallback(() => {
    collectorDisplayControllerRef.current?.stopCounterAnimation();
  }, []);

  // Expose imperative methods to parent via ref
  useImperativeHandle(ref, () => ({
    getController,
    showCollector,
    hideCollector,
    updateCollectorValue,
    enableSkipMode,
    disableSkipMode,
    setMaxWinCheck,
    startCounterAnimation,
    isCounterAnimating,
    stopCounterAnimation,
  }), [getController, showCollector, hideCollector, updateCollectorValue, enableSkipMode, disableSkipMode, setMaxWinCheck, startCounterAnimation, isCounterAnimating, stopCounterAnimation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (collectorDisplayControllerRef.current) {
        collectorDisplayControllerRef.current.cleanup();
      }
    };
  }, []);

  // CRITICAL: Don't render collector for completion reload (collection already finished)
  if (isHoldAndWinCompletionReload) {
    return null;
  }

  if (!dimensions || !dimensions.width || !dimensions.height) {
    return null;
  }

  return (
    <Container
      name="hold_and_win_collector_display"
      x={0}
      y={yOffset}
      sortableChildren={true}
      zIndex={8}
      ref={collectorDisplayContainerRef}
      visible={holdAndWinActive}
    />
  );
});

export default HoldAndWinCollectorDisplay;
